import { TeamPlayerDto } from 'modules/teams/domain/dtos/team-player.dto';
import { PlayerPosition } from 'modules/teams/domain/enums/player-position';
import { TeamPlayer } from 'modules/teams/domain/interfaces/team-player';
import type { TeamParticipant } from 'modules/teams/domain/interfaces/team-participant';
import { USER_DEFAULT_AVATAR } from 'shared/constants/links';
import { Role } from 'shared/types/role';

const POSITIONS = Object.values(PlayerPosition) as string[];
const ROLES: Role[] = ['STAFF', 'CAPTAIN', 'FAN'];

/**
 * GraphQL-энум `TeamPosition` отдаёт имя константы — `DEFENDER`, а доменный
 * `PlayerPosition` хранит значения в нижнем регистре. Без приведения регистра
 * ни одна позиция не совпадала и каждый участник схлопывался в MIDFIELDER.
 */
const mapPlayerPosition = (position?: string): PlayerPosition => {
  const normalized = position?.toLowerCase() ?? '';

  return POSITIONS.includes(normalized) ? (normalized as PlayerPosition) : PlayerPosition.MIDFIELDER;
};

/** `TeamRole` и доменный `Role` совпадают по значениям, регистр приводим на всякий случай. */
const mapTeamRole = (role?: string): Role | undefined => {
  const normalized = role?.toUpperCase() as Role | undefined;

  return normalized && ROLES.includes(normalized) ? normalized : undefined;
};

export const mapperPlayer = (player: Partial<TeamPlayer>): TeamPlayer => ({
  id: player.id ?? 0,
  userId: player.userId ?? 0,
  firstName: player.firstName ?? '',
  lastName: player.lastName ?? '',
  middleName: player.middleName ?? '',
  birthDate: player.birthDate ?? '',
  position: mapPlayerPosition(player.position),
  number: player.number ?? 99,
  avatar: player.avatar ?? USER_DEFAULT_AVATAR,
  isReal: player.isReal ?? false,
  height: player.height,
  weight: player.weight,
  role: player.role,
  createdAt: player.createdAt,
});

export function mapTeamPlayerToTeamParticipant(player: TeamPlayer): TeamParticipant {
  return {
    id: player.userId,
    name: player.firstName,
    surname: player.lastName,
    middleName: player.middleName,
    birthDate: player.birthDate,
    height: player.height,
    weight: player.weight,
    isReal: player.isReal,
    role: player.role,
    position: player.position,
    number: player.number,
    photo: player.avatar ? { id: 0, externalUrl: player.avatar } : undefined,
    // Раньше здесь был new Date(): результат менялся от вызова к вызову, и useMemo
    // на списке участников пересчитывался на каждый рендер.
    createdAt: player.createdAt ? new Date(player.createdAt) : new Date(0),
  };
}

export function mapPlayerDtoToPlayer(player: TeamPlayerDto): TeamPlayer {
  return mapperPlayer({
    id: player.id,
    userId: player.user?.id,
    firstName: player.user?.name,
    lastName: player.user?.surname,
    middleName: player.user?.middleName,
    birthDate: player.user?.birthDate,
    position: player.position as PlayerPosition | undefined,
    number: player.number,
    avatar: player.user?.photo?.externalUrl,
    isReal: player.user?.isReal,
    height: player.user?.height,
    weight: player.user?.weight,
    role: mapTeamRole(player.role),
    createdAt: player.createdAt,
  });
}
